import { Component, createRef } from 'react';
import { Calendar, ChevronDown, Check, Clock } from 'lucide-react';
import '../../styles/components/date-range.scss';

class DateRangeSelector extends Component {
  constructor(props) {
    super(props);

    const today = new Date();
    const last7 = new Date();
    last7.setDate(today.getDate() - 7);

    this.dropdownRef = createRef();

    this.state = {
      isOpen: false,
      selectedOption: 'Last 7 Days',
      startDate: this.formatDate(last7),
      endDate: this.formatDate(today),
    };
  }

  componentDidMount() {
    document.addEventListener('click', this.handleClickOutside);

    // Trigger initial onChange for default 30-day range
    const today = new Date();
    const last30 = new Date();
    last30.setDate(today.getDate() - 30);

    const { onChange } = this.props;
    if (onChange) {
      onChange({
        startDate: this.formatInputDate(last30),
        endDate: this.formatInputDate(today),
        option: 'Last 30 Days',
      });
    }
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  }

  componentDidUpdate(prevProps) {
    // Handle external clear trigger
    if (this.props.clearTrigger !== prevProps.clearTrigger && this.props.clearTrigger !== null) {
      const today = new Date();
      const last7 = new Date();
      last7.setDate(today.getDate() - 7);

      this.setState({
        selectedOption: 'Last 7 Days',
        startDate: this.formatDate(last7),
        endDate: this.formatDate(today),
      });

      if (this.props.onChange) {
        this.props.onChange({
          startDate: this.formatInputDate(last7),
          endDate: this.formatInputDate(today),
          option: 'Last 7 Days',
        });
      }
    }
  }

  formatDate(date) {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  }

  formatInputDate(date) {
    return date.toISOString().split('T')[0];
  }

  getDateRange(option) {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const last7Days = new Date(today);
    last7Days.setDate(last7Days.getDate() - 7);

    const last15Days = new Date(today);
    last15Days.setDate(last15Days.getDate() - 15);

    const last30Days = new Date(today);
    last30Days.setDate(last30Days.getDate() - 30);

    const lastMonth = new Date(today);
    lastMonth.setMonth(lastMonth.getMonth() - 1);

    switch (option) {
      case 'All':
        return { from: null, to: today };
      case 'Today':
        return { from: today, to: today };
      case 'Yesterday':
        return { from: yesterday, to: yesterday };
      case 'Last 7 Days':
        return { from: last7Days, to: today };
      case 'Last 15 Days':
        return { from: last15Days, to: today };
      case 'Last 30 Days':
        return { from: last30Days, to: today };
      default:
        return { from: today, to: today };
    }
  }

  handleClickOutside = (event) => {
    if (this.dropdownRef.current && !this.dropdownRef.current.contains(event.target)) {
      this.setState({ isOpen: false });
    }
  };

  toggleDropdown = () => {
    this.setState((prev) => ({ isOpen: !prev.isOpen }));
  };

  handleOptionSelect = (option) => {
    const { from, to } = this.getDateRange(option);
    const { onChange } = this.props;

    this.setState({
      selectedOption: option,
      startDate: from ? this.formatDate(from) : 'All',
      endDate: to ? this.formatDate(to) : 'All',
      isOpen: false,
    });

    if (onChange) {
      onChange({
        startDate: from ? this.formatInputDate(from) : null,
        endDate: to ? this.formatInputDate(to) : null,
        option,
      });
    }
  };

  render() {
    const { isOpen, selectedOption } = this.state;

    const options = [
      { key: 'All', label: 'All', icon: Calendar },
      { key: 'Today', label: 'Today', icon: Calendar },
      { key: 'Yesterday', label: 'Yesterday', icon: Clock },
      { key: 'Last 7 Days', label: 'Last 7 Days', icon: Calendar },
      { key: 'Last 15 Days', label: 'Last 15 Days', icon: Calendar },
      { key: 'Last 30 Days', label: 'Last 30 Days', icon: Calendar },
    ];

    return (
      <div className="pro-date-selector" ref={this.dropdownRef}>
        <div
          className={`pro-date-input ${isOpen ? 'pro-date-input--open' : ''}`}
          onClick={this.toggleDropdown}
        >
          <div className="pro-date-display">{selectedOption}</div>
          <ChevronDown
            className={`pro-dropdown-arrow ${isOpen ? 'pro-dropdown-arrow--open' : ''}`}
            size={20}
          />
        </div>

        {isOpen && (
          <div className="pro-dropdown-menu">
            <div className="pro-dropdown-header">Date Range Selection</div>
            {options.map((option) => {
              const IconComponent = option.icon;
              return (
                <div
                  key={option.key}
                  className={`pro-dropdown-option ${
                    selectedOption === option.key ? 'pro-dropdown-option--selected' : ''
                  }`}
                  onClick={() => this.handleOptionSelect(option.key)}
                >
                  <div className="pro-option-content">
                    <IconComponent className="pro-option-icon" size={16} />
                    <div className="pro-option-label">{option.label}</div>
                    {selectedOption === option.key && (
                      <Check className="pro-check-icon" size={16} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }
}

export default DateRangeSelector;
